import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Input } from 'antd';
import styled from 'styled-components';
import * as yup from 'yup';

import Form from '@components/form/form';
import FormButton from '@components/form/form-button';

const OtpGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;

  .ant-otp {
    gap: 12px;
  }

  .ant-otp-input {
    width: 64px;
    height: 64px;
    font-size: 28px;
    border-radius: 12px;
  }

  @media only screen and (max-width: 600px) {
    width: 100%;

    .ant-otp {
      gap: 6px;
      width: 100%;
      justify-content: center;
    }

    .ant-otp-input {
      width: 48px !important;
      height: 48px !important;
      min-height: unset !important;
      font-size: 18px;
      border-radius: 8px;
    }
  }

  @media only screen and (max-width: 480px) {
    .ant-otp-input {
      width: 46px !important;
      height: 46px !important;
      font-size: 16px;
    }
  }
`;

const ErrorText = styled.p`
  color: #ff4d4f;
  font-size: 13px;
  margin: -0.75em 0 1em;
  text-align: center;
`;

interface ResendTextProps {
  disabled?: boolean;
}

const ResendText = styled.p<ResendTextProps>`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};

  span {
    color: ${({ theme, disabled }) => (disabled ? theme.textSecondary : theme.primary)};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    font-weight: 600;
    &:hover {
      text-decoration: ${({ disabled }) => (disabled ? 'none' : 'underline')};
    }
  }
`;

export const otpSchema = yup.object({
  otp: yup
    .string()
    .required('Please enter the verification code')
    .length(6, 'Code must be exactly 6 digits')
});

export type OtpFormValues = yup.InferType<typeof otpSchema>;

interface OtpFormContentProps {
  isPending: boolean;
  isResending: boolean;
  cooldown: number;
  onResendClick: () => void;
}

function OtpFormContent({
  isPending,
  isResending,
  cooldown,
  onResendClick
}: OtpFormContentProps) {
  const intl = useIntl();
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext<OtpFormValues>();
  const otpValue = useWatch({ name: 'otp', control }) ?? '';

  const handleResendClick = () => {
    setValue('otp', '');
    onResendClick();
  };

  return (
    <>
      <OtpGroup>
        <Controller
          name="otp"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Input.OTP
              length={6}
              value={field.value}
              onChange={field.onChange}
              autoFocus
              size="large"
              style={{ marginBottom: errors.otp ? '0.5em' : '2em' }}
            />
          )}
        />
        {errors.otp && <ErrorText>{errors.otp.message}</ErrorText>}
        <FormButton
          type="primary"
          fullWidth
          withRightArrow
          loading={isPending}
          disabled={otpValue.length < 6 || isPending}
          htmlType="submit"
        >
          {intl.formatMessage({ id: 'OTP_SUBMIT' })}
        </FormButton>
      </OtpGroup>
      <ResendText disabled={cooldown > 0 || isResending}>
        {intl.formatMessage({ id: 'OTP_NO_CODE' })}{' '}
        <span onClick={handleResendClick}>
          {isResending
            ? intl.formatMessage({ id: 'OTP_SENDING' })
            : cooldown > 0
              ? intl.formatMessage({ id: 'OTP_RESEND_IN' }, { sec: cooldown })
              : intl.formatMessage({ id: 'OTP_RESEND' })}
        </span>
      </ResendText>
    </>
  );
}

export interface OtpFormProps extends OtpFormContentProps {
  onSubmit: (data: OtpFormValues) => Promise<void>;
}

function OtpForm({ onSubmit, ...contentProps }: OtpFormProps) {
  return (
    <Form schema={otpSchema} defaultValues={{ otp: '' }} onSubmit={onSubmit}>
      <OtpFormContent {...contentProps} />
    </Form>
  );
}

export default OtpForm;
